import { useEffect, useMemo, useState, type CSSProperties } from 'react'
import { buildGrainDataUri } from './texture/grain'
import { buildHexMeshDataUri } from './texture/hexMesh'
import { useTheme } from './ThemeProvider'

// The wash — the third of the three layers — is NOT here. Task 8 Step 3 set it
// from an effect on document.body; design spec §0.2 says "Wash is CSS", and the
// spec wins over the plan. It now lives in tokens.css (`--wash`) applied by
// reset.css, which paints it on the first frame rather than after hydration and
// keeps four inline properties off body, where they would outrank every future
// stylesheet. This component is the two generated layers its name describes.
//
// currentColor stands in for the mask's opaque stop — a mask only reads alpha,
// so the hue is irrelevant and a literal would trip the colour guard.
const MASK = [
  'radial-gradient(700px 420px at 12% 8%, currentColor 0%, transparent 70%)',
  'radial-gradient(520px 360px at 88% 22%, currentColor 0%, transparent 72%)',
  'radial-gradient(620px 460px at 48% 92%, currentColor 0%, transparent 70%)',
].join(',')

export function AppTexture() {
  const { resolved } = useTheme()
  const grain = useMemo(() => buildGrainDataUri(), [])

  // Rebuilt on theme change: a var() inside a data: URI never resolves, so the
  // ink has to be read out and baked in. The seed is fixed, so this is the same
  // mesh in a different ink — M0-R23 holds.
  const [ink, setInk] = useState('')
  useEffect(() => {
    // Read synchronously. This was a `queueMicrotask` until #75, because
    // ThemeProvider wrote `body[data-theme]` from a passive effect and React
    // runs a child's effects before its parent's — so at this point in the
    // mount commit the attribute was absent and `--hex-ink` still resolved to
    // the light default. `resolved` never changes again on its own, so that
    // wrong ink was permanent: a dark-preference user got light ink baked into
    // the mesh on every load, which is the failure hexMesh.ts's doc comment
    // warns about, reached from the other direction.
    //
    // The provider now writes the attribute from a LAYOUT effect, and layout
    // effects for the whole tree flush before any passive effect — so by the
    // time this runs the attribute is current, for a consumer at any depth.
    // The deferral is what is no longer needed; the ordering requirement it
    // served is unchanged, and the two tests below are what hold it.
    setInk(getComputedStyle(document.body).getPropertyValue('--hex-ink').trim())
  }, [resolved])
  const mesh = useMemo(() => (ink ? buildHexMeshDataUri({ ink }) : ''), [ink])

  const base: CSSProperties = {
    position: 'fixed',
    inset: 0,
    pointerEvents: 'none',
  }

  return (
    <>
      <div
        data-texture="mesh"
        aria-hidden
        style={{
          ...base,
          zIndex: -2,
          backgroundImage: mesh ? `url("${mesh}")` : undefined,
          backgroundRepeat: 'repeat',
          opacity: 'var(--mesh-opacity)',
          maskImage: MASK,
          WebkitMaskImage: MASK,
        }}
      />
      <div
        data-texture="grain"
        aria-hidden
        style={{
          ...base,
          zIndex: -1,
          backgroundImage: `url("${grain}")`,
          backgroundRepeat: 'repeat',
          // A token, like its sibling above — #76. The spec names no grain
          // opacity; the only place it appears is §0.2's remedy, "if grain ever
          // inverts that relationship, reduce grain opacity rather than
          // lightening --canvas", which is dark-theme-specific and so needs a
          // per-theme value. Held here as a literal it had no knob at all.
          opacity: 'var(--grain-opacity)',
          mixBlendMode: 'overlay',
        }}
      />
    </>
  )
}
